import { useState, useEffect, useRef } from "react";
import { useApp, api } from "../context";
import { Shell, Btn, TierBadge } from "../components";

const parseMarkdown = (text) => {
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br/>");
};

const JilinkOrb = ({ size = 36 }) => (
  <div style={{ width: size, height: size, borderRadius: "50%", background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: size * 0.45 }}>✦</div>
);

const QUICK_REPLIES = [
  "Someone to connect with deeply 💫",
  "Casual fun, no strings attached",
  "Long term relationship",
  "Friendship first, see where it goes",
  "Not sure yet, help me figure out 😊",
];

// ── CHAT TAB ──
const JilinkChatTab = () => {
  const { c, dark, user } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  }, [messages]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    const res = await api("/jilink/messages");
    setLoadingHistory(false);
    if (res.ok && res.data?.messages?.length > 0) {
      setMessages(res.data.messages.map(m => ({ role: m.role, content: m.content })));
    } else {
      // Get AI-generated personalized opener
      const openerRes = await api("/jilink/opener");
      const opener = openerRes.ok && openerRes.data?.opener
        ? openerRes.data.opener
        : "Hey " + (user?.name?.split(" ")[0] || "you") + "! 😏 Something told me you would show up today. What are you looking for?";
      setMessages([{ role: "assistant", content: opener }]);
    }
  };

  const sendMessage = async (text) => {
    const txt = (text || input).trim();
    if (!txt || loading) return;
    setInput("");
    const userMsg = { role: "user", content: txt };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
    const res = await api("/jilink/chat", { method: "POST", body: JSON.stringify({ messages: history }) });
    setLoading(false);
    if (res.ok && res.data?.reply) {
      setMessages(prev => [...prev, { role: "assistant", content: res.data.reply }]);
    } else {
      setMessages(prev => [...prev, { role: "assistant", content: "I'm having a moment! Try again shortly 😅" }]);
    }
  };

  const clearChat = async () => {
    if (!window.confirm("Clear chat history?")) return;
    await api("/jilink/history", { method: "DELETE" });
    setMessages([{
      role: "assistant",
      content: "Fresh start! 🌟 So, what are you looking for?"
    }]);
  };

  if (loadingHistory) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
      <JilinkOrb size={40} />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 220px)", minHeight: 400 }}>
      {/* Clear history button */}
      {messages.length > 3 && (
        <div style={{ textAlign: "right", marginBottom: 8 }}>
          <button onClick={clearChat} style={{ fontSize: 10, color: c.faint, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
            🗑 Clear history
          </button>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingBottom: 8 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", gap: 8, alignItems: "flex-start" }}>
            {msg.role === "assistant" && <JilinkOrb size={30} />}
            <div style={{
              maxWidth: "80%",
              padding: "10px 14px",
              borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "0 18px 18px 18px",
              background: msg.role === "user" ? "linear-gradient(135deg,#6c5ce7,#a855f7)" : (dark ? "#1e2035" : "#f4f0ff"),
              color: msg.role === "user" ? "#fff" : c.text,
              fontSize: 13,
              lineHeight: 1.6,
              border: msg.role === "assistant" ? "1px solid " + c.border : "none",
              whiteSpace: "pre-wrap",
            }}>
              <span dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }} />
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {loading && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <JilinkOrb size={30} />
            <div style={{ padding: "12px 16px", borderRadius: "0 18px 18px 18px", background: dark ? "#1e2035" : "#f4f0ff", border: "1px solid " + c.border, display: "flex", gap: 4, alignItems: "center" }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: 3, background: "#6c5ce7", animation: "jbounce 1.2s " + (i * 0.2) + "s infinite" }} />
              ))}
            </div>
          </div>
        )}

        {/* Quick replies - only on first message */}
        {messages.length === 1 && !loading && (
          <div style={{ paddingLeft: 38, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {QUICK_REPLIES.map((qr, i) => (
              <button key={i} onClick={() => sendMessage(qr)} style={{ padding: "6px 12px", borderRadius: 16, border: "1px solid " + c.border, background: c.card, color: c.sub, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                {qr}
              </button>
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ borderTop: "1px solid " + c.border, paddingTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
          placeholder="Tell Jilink what you're looking for..."
          disabled={loading}
          autoComplete="off"
          style={{ flex: 1, padding: "10px 14px", borderRadius: 22, border: "1px solid " + c.border, background: dark ? "#1e2035" : "#f4f0ff", color: c.text, fontSize: 13, outline: "none", fontFamily: "inherit" }}
        />
        <button
          onClick={() => sendMessage()}
          disabled={!input.trim() || loading}
          style={{ width: 40, height: 40, borderRadius: 20, border: "none", background: input.trim() ? "linear-gradient(135deg,#6c5ce7,#e91e8c)" : "#ccc", color: "#fff", cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/><path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      </div>
      <style>{`@keyframes jbounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-4px)}}`}</style>
    </div>
  );
};

// ── PROFILE TAB ──
const JilinkProfileTab = ({ onGoToChat }) => {
  const { c, dark, flash } = useApp();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    setLoading(true);
    const res = await api("/jilink/profile");
    setLoading(false);
    if (res.ok) setProfile(res.data?.profile);
  };

  const toggleConsent = async () => {
    const newVal = !profile?.consent_given;
    const res = await api("/jilink/profile/consent", { method: "POST", body: JSON.stringify({ consent: newVal }) });
    if (res.ok) { setProfile(p => ({ ...p, consent_given: newVal })); flash(newVal ? "You're now visible in matches!" : "Hidden from matches"); }
  };

  const deleteProfile = async () => {
    if (!window.confirm("Delete your Jilink profile? This removes you from all matches.")) return;
    await api("/jilink/profile", { method: "DELETE" });
    setProfile(null);
    flash("Profile deleted");
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: c.sub }}>Loading profile...</div>;

  if (!profile || !profile.profile_complete) return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <JilinkOrb size={56} />
      <div style={{ marginTop: 16, fontSize: 16, fontWeight: 700, color: c.text }}>No Profile Yet</div>
      <div style={{ fontSize: 12, color: c.sub, marginTop: 8, lineHeight: 1.6 }}>
        Chat with Jilink AI and share what you are looking for. Your profile builds automatically as you chat!
      </div>
      <Btn primary full onClick={onGoToChat} style={{ marginTop: 16 }}>Start Chatting 💬</Btn>
    </div>
  );

  const InfoCard = ({ icon, label, value }) => value ? (
    <div style={{ background: dark ? "#1e2035" : "#f4f0ff", borderRadius: 10, padding: "10px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 10, color: c.faint, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 13, color: c.text, fontWeight: 600 }}>{value}</div>
      </div>
    </div>
  ) : null;

  const TagRow = ({ icon, label, items, color }) => items?.length > 0 ? (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: c.faint, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{icon} {label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {items.map((item, i) => (
          <span key={i} style={{ padding: "4px 10px", borderRadius: 14, background: (color || "#6c5ce7") + "15", color: color || "#6c5ce7", fontSize: 11, fontWeight: 600, border: "1px solid " + (color || "#6c5ce7") + "30" }}>{item}</span>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div>
      <div style={{ background: "linear-gradient(135deg,#6c5ce720,#e91e8c10)", borderRadius: 14, padding: 16, marginBottom: 14, border: "1px solid #6c5ce730" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#6c5ce7", marginBottom: 4 }}>✦ Your Jilink Profile</div>
        <div style={{ fontSize: 11, color: c.sub }}>Built from your conversations with Jilink AI</div>
      </div>

      <InfoCard icon="💫" label="Looking For" value={profile.looking_for} />
      <InfoCard icon="💕" label="Gender Preference" value={profile.gender_preference} />
      <InfoCard icon="🎂" label="Age Range" value={profile.age_min && profile.age_max ? profile.age_min + " - " + profile.age_max + " years" : null} />
      <InfoCard icon="📍" label="Location" value={profile.location} />
      <InfoCard icon="⚡" label="Experience Level" value={profile.experience_level} />

      <TagRow icon="💜" label="Interests" items={profile.interests} color="#6c5ce7" />
      <TagRow icon="🔥" label="Kinks" items={profile.kinks} color="#ef4444" />

      {/* Consent toggle */}
      <div style={{ background: c.card, borderRadius: 14, border: "1px solid " + c.border, padding: 14, marginTop: 8, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Visible in Matches</div>
            <div style={{ fontSize: 11, color: c.sub, marginTop: 2, lineHeight: 1.4 }}>Let others find you. Private details stay hidden 🔒</div>
          </div>
          <button onClick={toggleConsent} style={{ width: 44, height: 24, borderRadius: 12, border: "none", background: profile.consent_given ? "#6c5ce7" : c.border, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
            <div style={{ width: 18, height: 18, borderRadius: 9, background: "#fff", position: "absolute", top: 3, left: profile.consent_given ? 23 : 3, transition: "left 0.2s" }} />
          </button>
        </div>
      </div>

      <button onClick={deleteProfile} style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: "1px solid #ef444430", background: "transparent", color: "#ef4444", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", marginTop: 4 }}>
        🗑 Delete My Profile
      </button>
    </div>
  );
};

// ── TINDER CARD STACK ──
const JilinkCardStack = ({ matches, user, dark, c, flash, go, setViewingUser, startConvo, canFollowFn, onGoToChat, onRefresh }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [passed, setPassed] = useState([]);
  const [hookedUp, setHookedUp] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const current = matches[currentIndex];
  const remaining = matches.length - currentIndex;

  const handlePass = () => {
    const targetId = current.id;
    setPassed(prev => [...prev, targetId]);
    api("/jilink/matches/" + targetId + "/react", { method: "POST", body: JSON.stringify({ status: "passed" }) }).catch(() => {});
    if (currentIndex < matches.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setCurrentIndex(matches.length); // done
    }
  };

  const handleHookUp = async () => {
    const targetId = current.id;
    setHookedUp(prev => [...prev, targetId]);
    api("/jilink/matches/" + targetId + "/react", { method: "POST", body: JSON.stringify({ status: "connected" }) }).catch(() => {});
    const myName = user?.name || "there";
    const myAge = user?.age ? `, age ${user.age}` : "";
    const myLocation = user?.location ? ` from ${user.location}` : "";
    const lookingFor = current.looking_for || "similar interests";
    const myInterests = user?.interests ? ` My interests include ${Array.isArray(user.interests) ? user.interests.join(", ") : user.interests}.` : "";

    const introMsg = `Hi ${current.name}! 👋 Jilink AI matched us together 💜\n\nMy name is ${myName}${myAge}${myLocation}.${myInterests}\n\nWe matched on: ${lookingFor}. I'd love to connect and get to know you better! 😊`;

    if (startConvo) {
      sessionStorage.setItem("gc_jilink_autosend", introMsg);
      await startConvo(current.id);
      flash("Connected! Sending intro message... 💜");
    } else {
      flash("Upgrade to message", "error");
    }

    if (currentIndex < matches.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setCurrentIndex(matches.length);
    }
  };

  // All cards done
  if (currentIndex >= matches.length) return (
    <div>
      <div style={{ textAlign: "center", padding: "30px 16px", background: dark ? "#1e2035" : "#f4f0ff", borderRadius: 20, marginBottom: 16, border: "1px solid " + c.border }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>✨</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#6c5ce7", marginBottom: 6 }}>You've seen all your matches!</div>
        <div style={{ fontSize: 12, color: c.sub, lineHeight: 1.5, marginBottom: 16 }}>
          {hookedUp.length > 0 ? `You connected with ${hookedUp.length} match${hookedUp.length > 1 ? "es" : ""}! Check your DMs 💬` : "No connections yet. New matches arrive daily!"}
        </div>
        <button onClick={() => { setCurrentIndex(0); if (onRefresh) onRefresh(); }} style={{ padding: "10px 24px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          🔄 Refresh Matches
        </button>
      </div>
    </div>
  );

  const u = current;

  return (
    <div>
      {/* Progress */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: c.sub }}>{currentIndex + 1} of {matches.length} matches</div>
        <div style={{ display: "flex", gap: 4 }}>
          {matches.map((_, i) => (
            <div key={i} style={{ width: i === currentIndex ? 16 : 6, height: 6, borderRadius: 3, background: i < currentIndex ? "#10b981" : i === currentIndex ? "#6c5ce7" : c.border, transition: "all 0.3s" }} />
          ))}
        </div>
      </div>

      {/* Card */}
      <div style={{ background: c.card, borderRadius: 24, border: "1px solid " + c.border, overflow: "hidden", marginBottom: 16, boxShadow: dark ? "0 8px 32px rgba(0,0,0,0.4)" : "0 8px 32px rgba(108,92,231,0.1)" }}>

        {/* Avatar section */}
        <div style={{ position: "relative", height: 280, background: u.avatar_url ? "transparent" : "linear-gradient(135deg,#6c5ce7,#e91e8c)", overflow: "hidden" }}>
          {u.avatar_url
            ? <img src={u.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80 }}>👤</div>
          }
          {/* Gradient overlay */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 140, background: "linear-gradient(transparent,rgba(0,0,0,0.85))", padding: "0 16px 14px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{u.name}</span>
              {u.is_verified && <span style={{ fontSize: 13, color: "#60a5fa" }}>✓</span>}
              {u.plan === "elite" && <span style={{ fontSize: 9, fontWeight: 800, color: "#f59e0b", background: "#f59e0b25", padding: "1px 6px", borderRadius: 6 }}>ELITE</span>}
              {u.plan === "premium" && <span style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", background: "#6c5ce725", padding: "1px 6px", borderRadius: 6 }}>PREMIUM</span>}
            </div>
            {(u.location || u.experience_level) && (
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
                {u.location && `📍 ${u.location}`}{u.location && u.experience_level && " · "}{u.experience_level && u.experience_level}
              </div>
            )}
          </div>
          {/* Match score badge */}
          <div style={{ position: "absolute", top: 14, right: 14, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", borderRadius: 12, padding: "6px 10px", textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: u.match_score >= 70 ? "#00d68f" : u.match_score >= 50 ? "#f5a623" : "#a78bfa" }}>{u.match_score}%</div>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.6)", fontWeight: 700 }}>MATCH</div>
          </div>
        </div>

        {/* Card body */}
        <div style={{ padding: "14px 16px" }}>

          {/* Match bar */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: c.faint, fontWeight: 700 }}>COMPATIBILITY</span>
              <span style={{ fontSize: 10, color: u.match_score >= 70 ? "#00d68f" : "#6c5ce7", fontWeight: 700 }}>{u.match_score >= 70 ? "Excellent" : u.match_score >= 50 ? "Good" : "Potential"}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: c.border, overflow: "hidden" }}>
              <div style={{ width: u.match_score + "%", height: "100%", borderRadius: 3, background: u.match_score >= 70 ? "linear-gradient(90deg,#00d68f,#10b981)" : u.match_score >= 50 ? "linear-gradient(90deg,#f5a623,#f59e0b)" : "linear-gradient(90deg,#6c5ce7,#e91e8c)", transition: "width 1s ease" }} />
            </div>
          </div>

          {/* Bio */}
          {u.bio && (
            <div style={{ fontSize: 12, color: c.sub, lineHeight: 1.6, marginBottom: 10, padding: "8px 12px", background: dark ? "#ffffff08" : "#f8f7ff", borderRadius: 10 }}>
              "{u.bio.slice(0, 120)}{u.bio.length > 120 ? "..." : ""}"
            </div>
          )}

          {/* Looking for */}
          {u.looking_for && (
            <div style={{ marginBottom: 10, fontSize: 12, color: c.text }}>
              <span style={{ color: c.faint, fontWeight: 700 }}>Looking for: </span>{u.looking_for}
            </div>
          )}

          {/* Match reasons */}
          {u.match_reasons?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 4 }}>
              {u.match_reasons.map((r, i) => (
                <span key={i} style={{ fontSize: 10, color: "#6c5ce7", background: dark ? "#6c5ce720" : "#f0edff", padding: "3px 10px", borderRadius: 10, border: "1px solid #6c5ce730", fontWeight: 600 }}>{r}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Jilink message */}
      <div style={{ background: dark ? "#1e2035" : "#f4f0ff", borderRadius: 14, padding: "10px 14px", marginBottom: 14, border: "1px solid " + c.border, display: "flex", gap: 10, alignItems: "flex-start" }}>
        <JilinkOrb size={24} />
        <div style={{ fontSize: 11, color: c.sub, lineHeight: 1.5, flex: 1 }}>
          <strong style={{ color: "#6c5ce7" }}>Jilink says:</strong> {u.match_score >= 70 ? `${u.name} is a strong match for you! You share key compatibility points.` : `${u.name} has potential. Your profiles align on some important areas.`}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={handlePass} style={{ flex: 1, padding: "14px 0", borderRadius: 16, border: "1.5px solid " + c.border, background: dark ? "#ffffff08" : "#fff", color: c.sub, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <span style={{ fontSize: 16 }}>👋</span> Kindly Pass
        </button>
        <button onClick={handleHookUp} style={{ flex: 2, padding: "14px 0", borderRadius: 16, border: "none", background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: "0 4px 16px rgba(108,92,231,0.4)" }}>
          <span style={{ fontSize: 16 }}>💜</span> Hook me up!
        </button>
      </div>

      {/* View profile link */}
      <div style={{ textAlign: "center", marginTop: 10 }}>
        <button onClick={() => { setViewingUser(u.id); go("user_profile"); }} style={{ background: "none", border: "none", color: "#6c5ce7", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          View full profile →
        </button>
      </div>
    </div>
  );
};

// ── MATCHES TAB ──
const JilinkMatchesTab = ({ onGoToProfile, onGoToChat }) => {
  const { c, dark, user, flash, go, setViewingUser, startConvo } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadMatches(); }, []);

  const loadMatches = async () => {
    setLoading(true);
    const res = await api("/jilink/matches");
    setLoading(false);
    if (res.ok) setData(res.data);
  };

  const myPlan = user?.plan || "free";
  const canFollowFn = (tp) => { if (myPlan === "elite") return true; if (myPlan === "premium" && tp !== "elite") return true; if (myPlan === "free" && tp === "free") return true; return false; };
  const doFollow = async (uid) => { const r = await api("/follow/" + uid, { method: "POST" }); if (r.ok) flash(r.data?.following ? "Following!" : "Unfollowed"); else flash("Upgrade to follow", "error"); };
  const doMessage = async (uid) => { if (startConvo) await startConvo(uid); else flash("Upgrade to message", "error"); };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: c.sub }}>Finding your matches... ✦</div>;

  // Free plan - locked
  if (data?.locked) return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>💎</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: c.text, marginBottom: 8 }}>Matches are Premium Only</div>
      <div style={{ fontSize: 12, color: c.sub, lineHeight: 1.6, marginBottom: 20 }}>
        Someone amazing could be waiting for you right now. Upgrade to Premium to see your top matches!
      </div>
      <div style={{ background: "linear-gradient(135deg,#6c5ce720,#e91e8c10)", borderRadius: 14, padding: 16, marginBottom: 16, border: "1px solid #6c5ce730" }}>
        <div style={{ fontSize: 12, color: "#6c5ce7", fontWeight: 700, marginBottom: 4 }}>✦ Premium — KES 499/month</div>
        <div style={{ fontSize: 11, color: c.sub }}>3 AI matches per month + unlimited messaging</div>
      </div>
      <Btn primary full onClick={() => go("premium")}>Upgrade to Premium 💎</Btn>
    </div>
  );

  // Needs profile
  if (data?.needs_profile) return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <JilinkOrb size={56} />
      <div style={{ marginTop: 16, fontSize: 16, fontWeight: 700, color: c.text }}>Set Up Your Profile First</div>
      <div style={{ fontSize: 12, color: c.sub, marginTop: 8, lineHeight: 1.6 }}>
        Chat with Jilink AI to build your profile. The more you share, the better your matches!
      </div>
      <Btn primary full onClick={onGoToChat} style={{ marginTop: 16 }}>Chat with Jilink 🤖</Btn>
    </div>
  );

  const matches = data?.matches || [];
  const plan = data?.plan;
  const matchesUsed = data?.matches_used;
  const matchesLimit = data?.matches_limit;

  return (
    <div>
      {/* Plan banner */}
      <div style={{ background: plan === "elite" ? "linear-gradient(135deg,#f59e0b20,#ef444410)" : "linear-gradient(135deg,#6c5ce720,#e91e8c10)", borderRadius: 12, padding: "10px 14px", marginBottom: 14, border: "1px solid " + (plan === "elite" ? "#f59e0b30" : "#6c5ce730"), display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: plan === "elite" ? "#f59e0b" : "#6c5ce7", fontWeight: 700 }}>
          {plan === "elite" ? "⚡ Elite — Unlimited Matches" : "💎 Premium Matches"}
        </span>
        {plan === "premium" && matchesLimit && (
          <span style={{ fontSize: 11, color: c.sub, fontWeight: 600 }}>{matchesUsed}/{matchesLimit} this month</span>
        )}
      </div>

      {matches.length === 0 ? (
        <div style={{ textAlign: "center", padding: "30px 16px" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🔮</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#6c5ce7", marginBottom: 6 }}>No matches yet</div>
          <div style={{ fontSize: 12, color: c.sub, lineHeight: 1.5, marginBottom: 16 }}>
            New members join daily! Keep chatting with Jilink to refine your profile.
          </div>
          <Btn primary full onClick={onGoToChat}>Chat with Jilink AI 🤖</Btn>
        </div>
      ) : (
        <JilinkCardStack matches={matches} user={user} dark={dark} c={c} flash={flash} go={go} setViewingUser={setViewingUser} startConvo={startConvo} canFollowFn={canFollowFn} onGoToChat={onGoToChat} onRefresh={loadMatches} />
      )}
    </div>
  );
};

// ── MAIN PAGE ──
export const LeaderboardPage = () => {
  const { c, dark } = useApp();
  const [tab, setTab] = useState("chat");

  const TABS = [
    { id: "chat", label: "🤖 Jilink AI" },
    { id: "profile", label: "📋 My Profile" },
    { id: "matches", label: "💑 Matches" },
  ];

  return (
    <Shell title="Jilink AI">
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#6c5ce7,#e91e8c)", borderRadius: 16, padding: "16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 48, height: 48, borderRadius: 24, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>✦</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>Jilink AI</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>Tell me what you want. I will find them.</div>
        </div>
        <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 10, padding: "4px 10px" }}>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.9)", fontWeight: 700 }}>AI POWERED</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {TABS.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)} style={{ flex: 1, padding: "9px 4px", borderRadius: 10, border: "1.5px solid " + (tab === tb.id ? "#6c5ce7" : c.border), background: tab === tb.id ? (dark ? "#6c5ce720" : "#f0edff") : c.card, color: tab === tb.id ? "#6c5ce7" : c.sub, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "chat" && <JilinkChatTab />}
      {tab === "profile" && <JilinkProfileTab onGoToChat={() => setTab("chat")} />}
      {tab === "matches" && <JilinkMatchesTab onGoToProfile={() => setTab("profile")} onGoToChat={() => setTab("chat")} />}
    </Shell>
  );
};